import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getColor } from 'common/constants/_colors';
import IterationTimeline from '../IterationGraph/IterationTimeline/IterationTimeline';
import { getTimelineData } from '../../pulse.service';
import Table from 'components/base/Table';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import { StuckHeader } from './styles';
import Icon from 'components/base/Icon';
import UnmatchedBranchesTooltip from './UnmatchedBranchesTooltip';

const UnMatchedBranchesTitleWrapper = styled(Flex)`
  flex-direction: column;
  width: 100%;
  background-color: ${getColor('white')};
  .MuiTableRow-head {
    border-top: 0 !important;
  }
  border-bottom: 1px solid ${getColor('catskillWhite')};
`;

const LinkedText = styled(Text)`
  z-index: 1;
`;

const ClosedStateWrapper = styled(Flex)`
  padding: 1.2rem;
`;

const IterationTimelineWrapper = styled(Flex)`
  height: 1.6rem;
`;

const Title = ({ branchesNumber, isOpen }) => {
  if (isOpen) {
    return (
      <Flex items='center'>
        <Text size='medium' ml='1rem'>
          {`${branchesNumber} unlinked ${
            branchesNumber === 1 ? 'branch' : 'branches'
          }`}
        </Text>
        <UnmatchedBranchesTooltip>
          <LinkedText
            color='chambray'
            ml='1rem'
            lineHeight='1.4rem'
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            why are branches unlinked?
          </LinkedText>
        </UnmatchedBranchesTooltip>
      </Flex>
    );
  }

  return (
    <ClosedStateWrapper items='center'>
      <Icon color='gray' name='ChevronRight' />
      <Text size='medium'>{`${branchesNumber} unlinked branches`}</Text>
      <Text ml='1rem' lineHeight='1.4rem' color='shuttleGray'>
        Your team is working on them, but they're not linked to any issues
      </Text>
      <UnmatchedBranchesTooltip>
        <LinkedText
          color='chambray'
          ml='1rem'
          lineHeight='1.4rem'
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          Learn more
        </LinkedText>
      </UnmatchedBranchesTooltip>
    </ClosedStateWrapper>
  );
};

const UnMatchedBranchesTitle = ({
  setTableHeight,
  tableHeight,
  iterations,
  workingDays,
  selectedContributorIds,
  branchesNumber,
  isOpen,
  scrollIntoUnmatchedBranches,
  setScrollIntoUnmatchedBranches
}) => {
  const contentRef = useRef();

  useEffect(() => {
    if (scrollIntoUnmatchedBranches && contentRef.current) {
      const timer = setTimeout(() => {
        contentRef.current.scrollIntoView({
          behavior: 'smooth'
        });
        setScrollIntoUnmatchedBranches(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [scrollIntoUnmatchedBranches]);

  const columns = [
    {
      name: '',
      dataPoint: 'branch',
      tableCellProps: {
        style: {
          width: '38%',
          minWidth: '20rem'
        }
      },
      ColumnHeaderComponent: () => (
        <Title branchesNumber={branchesNumber} isOpen={isOpen} />
      )
    },
    {
      name: 'PEOPLE',
      dataPoint: 'people',
      tableCellProps: {
        style: {
          width: '13.5%',
          minWidth: '10rem'
        }
      }
    },
    {
      name: 'LAST ACTIVITY',
      dataPoint: 'lastActivity',
      tableCellProps: {
        style: {
          width: '31%',
          minWidth: '10rem'
        }
      }
    },
    {
      name: '',
      dataPoint: 'timeline',
      tableCellProps: {
        style: {
          width: '45rem',
          padding: 0
        }
      },
      ColumnHeaderComponent: () => (
        <IterationTimelineWrapper>
          <IterationTimeline
            tableHeight={tableHeight}
            {...getTimelineData(iterations)}
            workingDays={workingDays}
            selectedContributorIds={selectedContributorIds}
            showTags={false}
          />
        </IterationTimelineWrapper>
      )
    },
    {
      name: '',
      dataPoint: 'stuck',
      tableCellProps: {
        style: {
          width: '9%',
          minWidth: '10rem',
          padding: '0.8rem 0'
        }
      },
      ColumnHeaderComponent: () => (
        <StuckHeader pl='1.2rem' pr='1.2rem'>
          ALERTS
        </StuckHeader>
      )
    }
  ];

  if (!isOpen) {
    return (
      <UnMatchedBranchesTitleWrapper>
        <Title branchesNumber={branchesNumber} isOpen={isOpen} />
      </UnMatchedBranchesTitleWrapper>
    );
  }

  return (
    <UnMatchedBranchesTitleWrapper
      ref={contentRef}
      onClick={() => setScrollIntoUnmatchedBranches(false)}
    >
      <Table
        tableComponentProps={{
          ref: el =>
            setTableHeight(`${el ? el.getBoundingClientRect().height : 0}px`)
        }}
        allowEmptyData={true}
        columns={columns}
        data={[]}
      />
    </UnMatchedBranchesTitleWrapper>
  );
};

export default UnMatchedBranchesTitle;
